/**
 * Gartner reprint pages (single-gartner-reprint.php).
 *
 * Sequence:
 *   1. The page renders, so the page view is tracked like any other page.
 *   2. Once boardflow is ready and the cookie banner, if any, has been answered,
 *      the resource download event is fired.
 *   3. Shortly after, the visitor is sent to the reprint link.
 *
 * The visible "click here" link is the fallback: it works at any time and its
 * click is tracked by boardflow-button-track-download-event.js through the
 * data-track-resource-download attribute.
 *
 * New tab or same tab follows the link's target attribute, set from Site
 * Options > Gartner Reprints. Browsers let a script open a tab only for a few
 * seconds after a real click (the cookie banner, typically); a synthetic
 * link.click() is subject to the same rule. So in new-tab mode the tab is
 * opened at once, before that window closes, and this page stays alive with
 * its tracking. When the browser blocks the tab (consent already stored, so
 * no click happened) the visitor is sent on in this tab after the delay.
 *
 * Consent: while the OneTrust banner is waiting for the visitor's choice the
 * redirect waits too, so the view and the download event are recorded with
 * consent before leaving. This is the behaviour of the reusable block this
 * script replaces. When the OneTrust script never shows up (blocked by the
 * browser, or not configured) there is no banner to wait for and the visitor
 * is sent on anyway.
 *
 * This module is part of theme.min.js and runs on every page: it does nothing
 * unless the page has an a[data-gartner-reprint] link.
 */

// Timings are set in Site Options > Gartner Reprints and printed by the
// template as data attributes on the link; these are the fallbacks.
const DEFAULTS = {
  // Time between firing the download event and leaving. Google events reach
  // GA4 through GTM and are sent with sendBeacon, which survives navigation;
  // Marketo (Munchkin) and Bing events are plain requests with no completion
  // callback, so this gap is what lets them finish before the page unloads.
  redirectDelay: 2500,
  // boardflow dispatches "ready" once. If it fired before this listener was
  // attached, or never fires, carry on after this delay.
  readyTimeout: 3000,
  // How long to give the OneTrust script to appear before treating it as absent.
  onetrustTimeout: 4000,
};

/**
 * Timings in milliseconds: data-redirect-delay, data-ready-timeout and
 * data-onetrust-timeout on the link, falling back to DEFAULTS.
 */
function readTimings(link) {
  const timings = {};
  for (const key of Object.keys(DEFAULTS)) {
    const value = Number.parseInt(link.dataset[key], 10);
    timings[key] = Number.isFinite(value) && value >= 0 ? value : DEFAULTS[key];
  }
  return timings;
}

/**
 * Whether the redirect may happen now, as far as cookie consent is concerned.
 *
 * boardflow.cookies is the OneTrust service provider; it is undefined when
 * OneTrust is not configured for the site. isCookieConsentSet() is false only
 * while the banner is still waiting for a choice.
 */
function consentAllowsRedirect(boardflow) {
  const cookies = boardflow.cookies;
  if (!cookies || typeof cookies.isCookieConsentSet !== "function") {
    return true;
  }
  return cookies.isCookieConsentSet() !== false;
}

document.addEventListener("DOMContentLoaded", () => {
  const link = document.querySelector("a[data-gartner-reprint]");
  if (!link || !link.href) {
    return;
  }
  const timings = readTimings(link);

  // Same download event the tracked buttons fire, defined in
  // boardflow-button-track-download-event.js (it already checks for boardflow).
  const trackDownload = () => window.board_trackResourceDownloadEvent?.();

  // redirect() can be reached from two paths (consent applied, or OneTrust
  // absent); only the first one counts.
  let done = false;
  const redirect = () => {
    if (done) return;
    done = true;

    if (link.target === "_blank") {
      // Open at once: the browser allows this only shortly after a click, and
      // this page stays alive anyway, so there is nothing to wait for.
      // Not "noopener" here: with it window.open always returns null, and the
      // return value is the only way to know whether the tab was blocked.
      const tab = window.open(link.href, "_blank");
      if (tab) {
        tab.opener = null;
        trackDownload();
        return;
      }
      // Blocked (no recent click): fall through and leave from this tab.
    }

    // Schedule the navigation first: tracking is best effort and must never
    // hold the visitor on this page.
    window.setTimeout(() => window.location.assign(link.href), timings.redirectDelay);
    trackDownload();
  };

  // No boardflow on the page: nothing to track or wait for.
  const boardflow = window.boardflow;
  if (!boardflow || typeof boardflow.on !== "function") {
    redirect();
    return;
  }

  // start() can be reached from the "ready" event or the ready timeout;
  // only the first one counts.
  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    if (consentAllowsRedirect(boardflow)) {
      redirect();
      return;
    }
    // Banner open: leave as soon as the visitor makes a choice, whatever it is.
    boardflow.on("onetrust.consent_applied", redirect, { once: true });
    // If the OneTrust script is not on the page by now, nobody will ever
    // answer that banner: leave anyway.
    window.setTimeout(() => {
      if (!window.OneTrust) redirect();
    }, timings.onetrustTimeout);
  };

  boardflow.on("ready", start, { once: true });
  window.setTimeout(start, timings.readyTimeout);
});
